// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SignInButton from "@adl/App/Components/Common/SignInButton";
import ProductAddToCartButton from "@adl/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, { ProductQuantityOrderedStyles } from "@adl/Overrides/Components/ProductQuantityOrdered";
import { color, xIsAuthenticated } from "@adl/XDL/Utilities";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    productQuantityOrderedStyles?: ProductQuantityOrderedStyles;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
}
export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    shouldWaitInventory?: true;
}

type Props = ProductActionsProps & HasProductContext;

const productActionsStyle: ProductActionsStyle = {
    addToCartContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        `,
    },
    qtyWrapper: {
        css: css`
            margin-right: 12px;
        `,
    },
    productQuantityOrderedStyles: {
        mergeCss: true,
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
            inputSelect: css`
                text-align: center;
                color: ${color("text")};
            `,
        },
        actionsStyles: {
            quantityOrderedTextField: {
                sizeVariant: "small",
                cssOverrides: {
                    inputSelect: css`
                        height: 28px;
                    `,
                },
            },
        },
    },
    addToCartButtonWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        sizeVariant: "small",
        css: css`
            width: 100%;
        `,
    },
};

const ProductActions: FC<Props> = ({ carouselId, iconOnly, extendedStyles, showActions = true, alwaysShowQty, onAddToCartClick, productContext, shouldWaitInventory = false }) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const isAuthenticated = useAppSelector(state => xIsAuthenticated(state));

    if (!isAuthenticated && !alwaysShowQty) {
        return <SignInButton extendedStyles={styles.productAddToCartButtonStyles} />;
    }

    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            <StyledWrapper {...styles.qtyWrapper}>
                <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                    {translate("QTY")}
                </VisuallyHidden>
                <ProductQuantityOrdered
                    labelOverride=""
                    extendedStyles={styles.productQuantityOrderedStyles}
                    showActions={showActions}
                    data-test-selector="productQty"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                {!isAuthenticated && <SignInButton extendedStyles={styles.productAddToCartButtonStyles} />}
                {isAuthenticated && (
                    <ProductAddToCartButton
                        onAddToCartClick={onAddToCartClick}
                        data-test-selector="addToCartBtn"
                        {...{ iconOnly }}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        isDisabled={shouldWaitInventory && (!productContext.productInfo.inventory || productContext.productInfo.inventory.additionalResults?.addToCart === "false" )}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(ProductActions);
